'use strict';

(() => {
  window.mx = window.mx || {};

  /**
   * Modal manager component for creating and controlling modal dialogs.
   * Supports different modal types, focus management, and drag gestures.
   */
  window.mx.components.MxModalManager = class extends window.mx.components.MxComponent {
    constructor() {
      super();

      this.dragState = {
        isDragging: false,
        startY: 0,
        currentY: 0,
        threshold: 0.2,
        lastTimestamp: 0,
        velocity: 0,
      };

      this.animationTimeout = null;
    }

    connectedCallback() {
      if (this.overlay) {
        this.events.add(this.overlay, 'click', this.close);
      }
    }

    get isOpen() {
      return this.modifiers.has('open');
    }

    get modalContainer() {
      return this.cache.get('dom', 'modalContainer', () => {
        const element = this.querySelector('.modal-container');
        if (!element) {
          console.warn('Modal container not found in MxModalManager');
        }

        return element;
      });
    }

    get overlay() {
      return this.cache.get('dom', 'overlay', () => this.querySelector('.modal-overlay'));
    }

    get focusElementList() {
      return this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    }

    get closeButtons() {
      return this.querySelectorAll('[data-role="close"]');
    }

    get pullHandle() {
      return this.cache.get('dom', 'pullHandle', () => this.querySelector('.modal-pull-handle'));
    }

    /**
     * Resets the modal to its initial state
     * @returns {this} For method chaining
     */
    reset = () => {
      if (this.modalContainer) {
        while (this.modalContainer.firstChild) {
          this.modalContainer.removeChild(this.modalContainer.firstChild);
        }
      }

      this.modifiers.clear();
      this.removeAttribute('data-template-id');

      if (this.animationTimeout) {
        clearTimeout(this.animationTimeout);
        this.animationTimeout = null;
      }

      return this;
    };

    /**
     * Creates a modal from a template element
     * @param {HTMLTemplateElement} template - The template to use for the modal content
     * @param {Object} [options={}] - Options for the modal
     * @param {string} [options.modifiers=''] - Modifiers to apply to the modal
     * @param {HTMLElement} [options.trigger=null] - Element that triggered the modal
     * @returns {this} For method chaining
     */
    create = (template, { modifiers = '', trigger = null } = {}) => {
      if (!template) {
        console.error('No template provided to create modal');
        return this;
      }

      const templateId = template.id;
      const currentTemplateId = this.getAttribute('data-template-id');

      if (templateId && currentTemplateId && templateId === currentTemplateId) {
        this.openTrigger = trigger;

        return this;
      }

      this.reset();

      if (!(template instanceof HTMLTemplateElement)) {
        console.error('Modal template must be an HTMLTemplateElement');
        return this;
      }

      if (!this.modalContainer) {
        console.error('Modal container not found');
        return this;
      }

      this.modalContainer.style.transition = 'none';

      const content = template.content.cloneNode(true);
      this.modalContainer.appendChild(content);

      if (!modifiers) {
        modifiers = template.getAttribute('data-modifiers') || '';
      }

      this.modifiers.add(modifiers);

      if (templateId) {
        this.setAttribute('data-template-id', templateId);
      }

      this.openTrigger = trigger;

      if (this.modifiers.has('drawer') && this.pullHandle) {
        this.events.add(this.pullHandle, 'pointerdown', this.onPointerDown);
      }

      if (this.closeButtons.length > 0) {
        this.closeButtons.forEach((button) => {
          this.events.add(button, 'click', this.close);
        });
      }

      setTimeout(() => {
        if (this.modalContainer) {
          this.modalContainer.style.transition = null;
        }
      }, 100);

      return this;
    };

    /**
     * Opens the modal
     * @returns {this} For method chaining
     */
    open = () => {
      if (this.isOpen) return this;

      this.modifiers.add('open');
      window.mx.utils.scroll.lock();

      this.events.add(window, 'keydown', this.onKeyDown);

      setTimeout(() => {
        const focusElement = this.focusElementList[0];

        if (focusElement) {
          focusElement.focus();
        } else if (this.modalContainer) {
          this.modalContainer.setAttribute('tabindex', '-1');
          this.modalContainer.focus();
        }
      }, 100);

      this.dispatchEvent(
        new CustomEvent('mx-modal:open', {
          detail: { modal: this },
          bubbles: true,
        }),
      );

      return this;
    };

    /**
     * Closes the modal
     * @returns {this} For method chaining
     */
    close = () => {
      if (!this.isOpen) return this;

      this.modifiers.remove('open');
      this.removeAttribute('data-open');
      window.mx.utils.gorgias.show();
      window.mx.utils.scroll.unlock();

      this.events.remove(window, 'keydown', this.onKeyDown);

      if (this.openTrigger && this.openTrigger.focus) {
        this.openTrigger.focus();
      }

      this.dispatchEvent(
        new CustomEvent('mx-modal:close', {
          detail: { modal: this },
          bubbles: true,
        }),
      );

      return this;
    };

    /**
     * Handles keyboard events for the modal
     * @private
     * @param {KeyboardEvent} event - The keyboard event
     */
    onKeyDown = (event) => {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        const focusElementList = this.focusElementList;

        if (focusElementList.length === 0) return;

        const firstElement = focusElementList[0];
        const lastElement = focusElementList[focusElementList.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }

      if (['ArrowLeft', 'ArrowRight', 'Enter'].includes(event.key)) {
        this.dispatchEvent(
          new CustomEvent('mx-modal:key', {
            bubbles: true,
            detail: { key: event.key },
          }),
        );
      }
    };

    /**
     * Handles pointer down events for drawer-type modals
     * @private
     * @param {PointerEvent} event - The pointer event
     */
    onPointerDown = (event) => {
      if (!this.modifiers.has('drawer')) return;

      if (event.pointerType === 'touch') {
        event.preventDefault();
        if (event.target.setPointerCapture) {
          event.target.setPointerCapture(event.pointerId);
        }
      }

      this.dragState.isDragging = true;
      this.dragState.startY = event.clientY;
      this.dragState.currentY = 0;
      this.dragState.lastTimestamp = event.timeStamp;
      this.dragState.velocity = 0;

      if (this.modalContainer) {
        this.modalContainer.style.transition = 'none';
      }

      this.events.add(window, 'pointermove', this.onPointerMove, { passive: false });
      this.events.add(window, 'pointerup', this.onPointerUp);
      this.events.add(window, 'pointercancel', this.onPointerUp);
    };

    /**
     * Handles pointer move events for drawer-type modals
     * @private
     * @param {PointerEvent} event - The pointer event
     */
    onPointerMove = (event) => {
      if (!this.dragState.isDragging) return;

      event.preventDefault();

      const deltaTime = event.timeStamp - this.dragState.lastTimestamp;
      if (deltaTime > 0) {
        const instantVelocity = (event.clientY - this.dragState.startY - this.dragState.currentY) / deltaTime;
        this.dragState.velocity = 0.7 * this.dragState.velocity + 0.3 * instantVelocity;
      }
      this.dragState.lastTimestamp = event.timeStamp;

      const isBottomDrawer = this.modifiers.has('bottom');
      const isTopDrawer = this.modifiers.has('top');

      const deltaY = event.clientY - this.dragState.startY;

      if ((isBottomDrawer && deltaY < 0) || (isTopDrawer && deltaY > 0)) return;

      this.dragState.currentY = deltaY;

      if (this.modalContainer) {
        this.modalContainer.style.transform = `translateY(${deltaY}px)`;
      }
    };

    /**
     * Handles pointer up events for drawer-type modals
     * @private
     * @param {PointerEvent} event - The pointer event
     */
    onPointerUp = (event) => {
      if (!this.dragState.isDragging) return;

      if (
        event.pointerType === 'touch' &&
        event.target.hasPointerCapture &&
        event.target.hasPointerCapture(event.pointerId)
      ) {
        event.target.releasePointerCapture(event.pointerId);
      }

      this.dragState.isDragging = false;

      this.events.remove(window, 'pointermove', this.onPointerMove);
      this.events.remove(window, 'pointerup', this.onPointerUp);
      this.events.remove(window, 'pointercancel', this.onPointerUp);

      if (!this.modalContainer) return;

      const modalHeight = this.modalContainer.offsetHeight;
      const dragPercentage = Math.abs(this.dragState.currentY) / modalHeight;
      const velocityThreshold = 0.5;

      if (dragPercentage > this.dragState.threshold || Math.abs(this.dragState.velocity) > velocityThreshold) {
        this.close();
      } else {
        this.modalContainer.style.transition = null;
        this.modalContainer.style.transform = null;

        if (this.overlay) {
          this.overlay.style.opacity = null;
        }
      }

      this.animationTimeout = setTimeout(() => {
        if (this.modalContainer) {
          this.modalContainer.style.transition = null;
          this.modalContainer.style.transform = null;
        }

        if (this.overlay) {
          this.overlay.style.opacity = null;
        }

        this.animationTimeout = null;
      }, 200);
    };
  };

  if (!window.customElements.get('mx-modal-manager')) {
    window.customElements.define('mx-modal-manager', window.mx.components.MxModalManager);
  }
})();
